// src/hooks/usePayments.js
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// ⬇️ Adjust this import to your actual Supabase client path
import { supabase } from '../supabaseClient';

/* -----------------------------------------------------------------------------
 * Cache keys
 * ---------------------------------------------------------------------------*/
export const paymentsKey = (studentId) =>
  studentId ? ['payments', String(studentId)] : ['payments_list'];

export const otherChargesKey = (paymentId) => ['payment_other_charges', String(paymentId)];

export const paymentTotalsKey = (paymentId) => ['payment_totals', String(paymentId)];

/* -----------------------------------------------------------------------------
 * READ: fetch payments (optionally by student)
 * Left-joins students to get full_name for display; also returns student_name
 * ---------------------------------------------------------------------------*/
async function fetchPayments({ studentId }) {
  let query = supabase
    .from('payments')
    .select(`
      id,
      student_id,
      invoice_number,
      amount,
      payment_method,
      payment_date,
      status,
      payment_type,
      is_deleted,
      receipt_url,
      notes,
      created_at,
      students:students (
        id,
        full_name
      )
    `)
    .eq('is_deleted', false)
    .order('payment_date', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });

  if (studentId) {
    query = query.eq('student_id', studentId);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  return (data || []).map((p) => ({
    ...p,
    student_name: p.student_name ?? p?.students?.full_name ?? null,
  }));
}

/* -----------------------------------------------------------------------------
 * WRITE HELPERS (payments)
 * - createPayment: returns { ok, data } or { ok:false, code, error }
 * - updatePaymentById: throws on error (used in edit via a separate hook)
 * - softDeletePaymentById: throws on error
 * ---------------------------------------------------------------------------*/
async function createPayment(payload) {
  try {
    // Optional: whitelist allowed columns
    const validColumns = [
      'student_id',
      'invoice_number',
      'amount',
      'payment_method',
      'payment_date',
      'status',
      'payment_type',
      'notes',
      'receipt_url',
      'is_deleted',
    ];
    const insertData = {};
    for (const k in payload) {
      if (validColumns.includes(k)) insertData[k] = payload[k];
    }

    const { data, error } = await supabase
      .from('payments')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      return {
        ok: false,
        error: error.message || 'Failed to create payment',
        code: error.code, // e.g. '23505' for unique constraint
        details: error.details,
      };
    }
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e?.message || 'Unexpected error while creating payment' };
  }
}

async function updatePaymentById(id, patch) {
  const { data, error } = await supabase
    .from('payments')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

async function softDeletePaymentById(id) {
  const { data, error } = await supabase
    .from('payments')
    .update({ is_deleted: true })
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

/* -----------------------------------------------------------------------------
 * WRITE HELPERS (payment_enrollments)
 * ---------------------------------------------------------------------------*/
async function linkPaymentEnrollment({ payment_id, enrollment_id, amount_applied = 0 }) {
  const { data, error } = await supabase
    .from('payment_enrollments')
    .insert({ payment_id, enrollment_id, amount_applied })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

async function linkPaymentEnrollmentsBulk(rows) {
  const normalized = rows.map((r) => ({ ...r, amount_applied: r.amount_applied ?? 0 }));
  const { data, error } = await supabase.from('payment_enrollments').insert(normalized).select();
  if (error) throw new Error(error.message);
  return data;
}

/* -----------------------------------------------------------------------------
 * READ HELPERS (payment_other_charges) — Option A
 * ---------------------------------------------------------------------------*/
async function fetchOtherCharges(paymentId) {
  if (!paymentId) throw new Error('paymentId is required');
  const { data, error } = await supabase
    .from('payment_other_charges')
    .select('id, payment_id, category, description, amount, created_at')
    .eq('payment_id', paymentId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

/* -----------------------------------------------------------------------------
 * WRITE HELPERS (payment_other_charges)
 * ---------------------------------------------------------------------------*/
async function insertOtherCharge({ paymentId, category, description, amount }) {
  if (!paymentId) throw new Error('paymentId is required');
  if (!category) throw new Error('category is required');
  if (!description) throw new Error('description is required');
  const amt = Number(amount ?? 0);
  if (!(amt >= 0)) throw new Error('amount must be >= 0');

  const { data, error } = await supabase
    .from('payment_other_charges')
    .insert([{ payment_id: paymentId, category, description, amount: amt }])
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

async function insertOtherChargesBulk({ paymentId, rows }) {
  if (!paymentId) throw new Error('paymentId is required');
  const payload = (rows || []).map((r) => ({
    payment_id: paymentId,
    category: r.category,
    description: r.description,
    amount: Number(r.amount ?? 0),
  }));
  if (!payload.length) return [];

  const { data, error } = await supabase.from('payment_other_charges').insert(payload).select();

  if (error) throw new Error(error.message);
  return data || [];
}

// ✅ Accepts either a string id or an object { id, paymentId }
async function deleteOtherChargeById(idOrObj) {
  const id = typeof idOrObj === 'object' && idOrObj !== null ? idOrObj.id : idOrObj;
  if (!id) throw new Error('id is required');
  const { error } = await supabase.from('payment_other_charges').delete().eq('id', id);
  if (error) throw new Error(error.message);
  return { id };
}

/* ✅ UPDATE helper for inline editing */
async function updateOtherChargeById({ id, patch }) {
  if (!id) throw new Error('id is required');
  const clean = {};
  if (patch.category !== undefined) clean.category = patch.category;
  if (patch.description !== undefined) clean.description = patch.description;
  if (patch.amount !== undefined) clean.amount = Number(patch.amount);

  const { data, error } = await supabase
    .from('payment_other_charges')
    .update(clean)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
}

/* -----------------------------------------------------------------------------
 * Helper: look up a payment's student_id (for invalidation of paid pill)
 * ---------------------------------------------------------------------------*/
async function fetchPaymentStudentId(paymentId) {
  const { data, error } = await supabase
    .from('payments')
    .select('student_id')
    .eq('id', paymentId)
    .single();
  if (error) throw new Error(error.message);
  return data?.student_id ?? null;
}

/* -----------------------------------------------------------------------------
 * READ: optional totals view (safe if missing)
 * ---------------------------------------------------------------------------*/
async function fetchPaymentTotals(paymentId) {
  if (!paymentId) throw new Error('paymentId is required');
  try {
    const { data, error } = await supabase
      .from('v_payment_totals')
      .select('payment_id, enrollments_total, other_charges_total, items_subtotal, payment_amount')
      .eq('payment_id', paymentId)
      .maybeSingle();
    if (error) throw error;
    return data || null;
  } catch (_e) {
    // If the view hasn't been created, fail soft
    return null;
  }
}

/* -----------------------------------------------------------------------------
 * Hook: usePayments(studentId?)
 * Exposes:
 *   - payments, loading, fetching, error
 *   - addPayment (Create)  -> resolves to { ok, data? , error?, code? }
 *   - updatePayment        -> resolves to updated row
 *   - softDeletePayment    -> resolves to deleted row
 *   - deletePayment        -> alias that returns { success, error? } for legacy callers
 *   - addPaymentEnrollment / addPaymentEnrollments
 *   - other charges hooks (list/add/bulk/delete/update)
 *   - payment totals (from view, if present)
 * ---------------------------------------------------------------------------*/
export function usePayments(studentId) {
  const qc = useQueryClient();

  // bust Paid/Not Paid pills for a student
  const bustPaidStatus = (sid) => {
    if (!sid) return;
    const key = ['paid_enrollment_ids', String(sid)];
    qc.invalidateQueries({ queryKey: key });
    qc.refetchQueries({ queryKey: key });
  };

  /* READ */
  const {
    data: payments = [],
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: paymentsKey(studentId),
    queryFn: () => fetchPayments({ studentId }),
    enabled: true,
    staleTime: 0,                        // always consider stale
    refetchOnMount: 'always',            // refresh when (re)mounting after edits
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  /* CREATE (optimistic) */
  const addPaymentMutation = useMutation({
    mutationFn: (payload) => createPayment(payload),
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: paymentsKey(payload.student_id) });
      await qc.cancelQueries({ queryKey: paymentsKey(undefined) });

      const perStudentKey = paymentsKey(payload.student_id);
      const globalKey = paymentsKey(undefined);

      const prevStudent = qc.getQueryData(perStudentKey);
      const prevGlobal = qc.getQueryData(globalKey);

      // Optimistic insert
      const temp = {
        ...payload,
        id: `temp-${Date.now()}`,
        is_deleted: false,
      };

      qc.setQueryData(perStudentKey, (old = []) => [temp, ...old]);
      qc.setQueryData(globalKey, (old = []) => [temp, ...old]);

      return { prevStudent, prevGlobal, perStudentKey, globalKey, payload };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      qc.setQueryData(ctx.perStudentKey, ctx.prevStudent);
      qc.setQueryData(ctx.globalKey, ctx.prevGlobal);
    },
    onSuccess: (result, _vars, ctx) => {
      if (!ctx) return;

      if (!result?.ok) {
        qc.setQueryData(ctx.perStudentKey, ctx.prevStudent);
        qc.setQueryData(ctx.globalKey, ctx.prevGlobal);
        return;
      }

      const created = result.data;
      const replaceTemp = (list = []) => list.map((p) => (String(p.id).startsWith('temp-') ? created : p));
      qc.setQueryData(ctx.perStudentKey, replaceTemp);
      qc.setQueryData(ctx.globalKey, replaceTemp);

      bustPaidStatus(created?.student_id);
    },
    onSettled: (_result, _err, ctx) => {
      const sid = ctx?.payload?.student_id;
      qc.invalidateQueries({ queryKey: paymentsKey(sid) });
      qc.invalidateQueries({ queryKey: paymentsKey(undefined) });
    },
  });

  /* UPDATE (optimistic patch) */
  const updatePaymentMutation = useMutation({
    mutationFn: ({ id, patch }) => updatePaymentById(id, patch),
    onMutate: async ({ id, patch, student_id }) => {
      const perStudentKey = paymentsKey(student_id);
      const globalKey = paymentsKey(undefined);

      await qc.cancelQueries({ queryKey: perStudentKey });
      await qc.cancelQueries({ queryKey: globalKey });

      const prevStudent = qc.getQueryData(perStudentKey);
      const prevGlobal = qc.getQueryData(globalKey);

      const applyPatch = (list = []) => list.map((p) => (p.id === id ? { ...p, ...patch } : p));
      qc.setQueryData(perStudentKey, applyPatch);
      qc.setQueryData(globalKey, applyPatch);

      return { prevStudent, prevGlobal, perStudentKey, globalKey };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      qc.setQueryData(ctx.perStudentKey, ctx.prevStudent);
      qc.setQueryData(ctx.globalKey, ctx.prevGlobal);
    },
    onSuccess: (updated) => {
      bustPaidStatus(updated?.student_id);
    },
    onSettled: (_res, _err, vars) => {
      qc.invalidateQueries({ queryKey: paymentsKey(vars?.student_id) });
      qc.invalidateQueries({ queryKey: paymentsKey(undefined) });
    },
  });

  /* SOFT DELETE (optimistic remove) */
  const softDeletePaymentMutation = useMutation({
    mutationFn: ({ id }) => softDeletePaymentById(id),
    onMutate: async ({ id, student_id }) => {
      const perStudentKey = paymentsKey(student_id);
      const globalKey = paymentsKey(undefined);

      await qc.cancelQueries({ queryKey: perStudentKey });
      await qc.cancelQueries({ queryKey: globalKey });

      const prevStudent = qc.getQueryData(perStudentKey);
      const prevGlobal = qc.getQueryData(globalKey);

      const remove = (list = []) => list.filter((p) => p.id !== id);
      qc.setQueryData(perStudentKey, remove);
      qc.setQueryData(globalKey, remove);

      return { prevStudent, prevGlobal, perStudentKey, globalKey, student_id };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      qc.setQueryData(ctx.perStudentKey, ctx.prevStudent);
      qc.setQueryData(ctx.globalKey, ctx.prevGlobal);
    },
    onSuccess: (deletedRow, _vars, ctx) => {
      const sid = deletedRow?.student_id ?? ctx?.student_id;
      bustPaidStatus(sid);
    },
    onSettled: (_res, _err, vars) => {
      qc.invalidateQueries({ queryKey: paymentsKey(vars?.student_id) });
      qc.invalidateQueries({ queryKey: paymentsKey(undefined) });
    },
  });

  /* LINK HELPERS (enrollments) */
  const addPaymentEnrollmentMutation = useMutation({
    mutationFn: (row) => linkPaymentEnrollment(row),
    onSuccess: async (_res, vars) => {
      try {
        const sid = await fetchPaymentStudentId(vars.payment_id);
        bustPaidStatus(sid);
      } catch {
        // ignore
      }
    },
  });

  const addPaymentEnrollmentsMutation = useMutation({
    mutationFn: (rows) => linkPaymentEnrollmentsBulk(rows),
    onSuccess: async (_res, rows) => {
      try {
        const firstPaymentId = rows?.[0]?.payment_id;
        if (firstPaymentId) {
          const sid = await fetchPaymentStudentId(firstPaymentId);
          bustPaidStatus(sid);
        }
      } catch {
        // ignore
      }
    },
  });

  /* READ: other charges list */
  function useOtherCharges(paymentId, options = {}) {
    return useQuery({
      queryKey: otherChargesKey(paymentId),
      queryFn: () => fetchOtherCharges(paymentId),
      enabled: !!paymentId,
      ...options,
    });
  }

  /* WRITE: other charges mutations */
  const addOtherChargeMutation = useMutation({
    mutationFn: insertOtherCharge,
    onSuccess: (created) => {
      const pid = created?.payment_id;
      if (pid) {
        qc.invalidateQueries({ queryKey: otherChargesKey(pid) });
        qc.invalidateQueries({ queryKey: paymentTotalsKey(pid) });
      }
    },
  });

  const addOtherChargesBulkMutation = useMutation({
    mutationFn: ({ paymentId, rows }) => insertOtherChargesBulk({ paymentId, rows }),
    onSuccess: (_createdRows, vars) => {
      const pid = vars?.paymentId;
      if (pid) {
        qc.invalidateQueries({ queryKey: otherChargesKey(pid) });
        qc.invalidateQueries({ queryKey: paymentTotalsKey(pid) });
      }
    },
  });

  // ✅ Option B: accepts either 'id' or { id, paymentId }
  const deleteOtherChargeMutation = useMutation({
    mutationFn: (vars) => deleteOtherChargeById(vars),
    onSuccess: (_res, vars) => {
      const paymentId =
        typeof vars === 'object' && vars !== null ? vars.paymentId : undefined;

      if (paymentId) {
        qc.invalidateQueries({ queryKey: otherChargesKey(paymentId) });
        qc.invalidateQueries({ queryKey: paymentTotalsKey(paymentId) });
      } else {
        // Fallback broad invalidation when we don't know the payment
        qc.invalidateQueries({ queryKey: ['payment_other_charges'] });
      }
    },
  });

  /* UPDATE: other charge (inline edit) */
  const updateOtherChargeMutation = useMutation({
    mutationFn: updateOtherChargeById, // expects { id, patch, paymentId? }
    onSuccess: (_updated, vars) => {
      const pid = vars?.paymentId;
      if (pid) {
        qc.invalidateQueries({ queryKey: otherChargesKey(pid) });
        qc.invalidateQueries({ queryKey: paymentTotalsKey(pid) });
      } else {
        qc.invalidateQueries({ queryKey: ['payment_other_charges'] });
      }
    },
  });

  /* READ: payment totals (view optional) */
  function usePaymentTotals(paymentId, options = {}) {
    return useQuery({
      queryKey: paymentTotalsKey(paymentId),
      queryFn: () => fetchPaymentTotals(paymentId),
      enabled: !!paymentId,
      ...options,
    });
  }

  /* Exposed API */
  return useMemo(
    () => ({
      // data
      payments,
      loading: isLoading,
      fetching: isFetching,
      error: error ? error.message || String(error) : null,

      // payments mutations
      addPayment: (payload) => addPaymentMutation.mutateAsync(payload),
      updatePayment: ({ id, patch, student_id }) =>
        updatePaymentMutation.mutateAsync({ id, patch, student_id }),
      softDeletePayment: ({ id, student_id }) =>
        softDeletePaymentMutation.mutateAsync({ id, student_id }),

      // 🔁 Legacy alias: returns { success, error? } and accepts id OR { id, student_id }
      deletePayment: async (idOrObj) => {
        try {
          const { id, student_id } =
            typeof idOrObj === 'object' && idOrObj !== null
              ? idOrObj
              : { id: idOrObj, student_id: undefined };
          await softDeletePaymentMutation.mutateAsync({ id, student_id });
          return { success: true };
        } catch (e) {
          return { success: false, error: e?.message || String(e) };
        }
      },

      // enrollment linking helpers
      addPaymentEnrollment: (row) => addPaymentEnrollmentMutation.mutateAsync(row),
      addPaymentEnrollments: (rows) => addPaymentEnrollmentsMutation.mutateAsync(rows),

      // OTHER CHARGES (Option A)
      useOtherCharges, // hook: const { data } = useOtherCharges(paymentId)
      addOtherCharge: (vars) => addOtherChargeMutation.mutateAsync(vars),
      addOtherChargesBulk: ({ paymentId, rows }) =>
        addOtherChargesBulkMutation.mutateAsync({ paymentId, rows }),
      deleteOtherCharge: (args) => deleteOtherChargeMutation.mutateAsync(args), // id OR { id, paymentId }
      updateOtherCharge: (vars) => updateOtherChargeMutation.mutateAsync(vars), // { id, patch, paymentId? }

      // Totals (view-backed, safe if missing)
      usePaymentTotals, // hook: const { data } = usePaymentTotals(paymentId)
    }),
    [
      payments,
      isLoading,
      isFetching,
      error,

      addPaymentMutation,
      updatePaymentMutation,
      softDeletePaymentMutation,

      addPaymentEnrollmentMutation,
      addPaymentEnrollmentsMutation,

      addOtherChargeMutation,
      addOtherChargesBulkMutation,
      deleteOtherChargeMutation,
      updateOtherChargeMutation,
    ]
  );
}
